import React, { useEffect, useState } from "react";
import CommonSelect from "../../../core/common/commonSelect";
import PredefinedDateRanges from "../../../core/common/datePicker";
import moment from "moment";

interface FilterPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (filters: EmployeeFilters) => void;
    onReset: () => void;
    initialFilters: EmployeeFilters;
    data: {
        allCompanies: any[];
        allDepartments: string[];
        allDesignations: any[];
    };
}

export interface EmployeeFilters {
    status: string;
    department: string;
    designation: string;
    sortBy: string;
    joiningDate: {
        start: string | null;
        end: string | null;
    } | null;
    createdDate: {
        start: string | null;
        end: string | null;
    } | null;
    company: {
        id: number | null;
        name: string;
        type: string;
    } | null;
    gender: string;
}

const EmployeeFilterPanel: React.FC<FilterPanelProps> = ({
    isOpen,
    onClose,
    onApply,
    onReset,
    initialFilters,
    data
}) => {
    const [tempFilters, setTempFilters] = useState<EmployeeFilters>(initialFilters);
    const [selectKey, setSelectKey] = useState(0);

    // Sync when panel opens
    useEffect(() => {
        if (isOpen) {
            const updatedFilters = { ...initialFilters };

            if (!updatedFilters.company && data.allCompanies.length > 0) {
                updatedFilters.company = {
                    id: data.allCompanies[0].id,
                    name: data.allCompanies[0].name,
                    type: data.allCompanies[0].type
                };
            }

            setTempFilters(updatedFilters);
            setSelectKey(prev => prev + 1);
        }
    }, [isOpen, initialFilters, data.allCompanies]);

    const genderOptions = [
        { value: "All Genders", label: "All Genders" },
        { value: "M", label: "Male" },
        { value: "F", label: "Female" },
        { value: "O", label: "Other" }
    ];

    // Status options
    const statusOptions = [
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
        { value: "Suspended", label: "Suspended" },
        { value: "All", label: "All Status" }
    ];

    // Sort options
    const sortOptions = [
        { value: "Recently Added", label: "Recently Added" },
        { value: "Ascending", label: "Ascending (A-Z)" },
        { value: "Descending", label: "Descending (Z-A)" },
        { value: "Last Month", label: "Last Month" },
        { value: "Last 7 Days", label: "Last 7 Days" }
    ];

    // Department options
    const departmentOptions = [
        { value: "All Departments", label: "All Departments" },
        ...(data.allDepartments || []).map(dept => ({
            value: dept,
            label: dept
        }))
    ];

    // Designation options
    const designationOptions = [
        { value: "All Designations", label: "All Designations" },
        ...(data.allDesignations || []).map(desig => ({
            value: desig.Designation,
            label: desig.Designation
        }))
    ];

    // Company options
    const companyOptions = [
        ...(data.allCompanies || []).map(company => ({
            value: `${company.id}-${company.type}`,
            label: `${company.name} - ${company.type}`
        }))
    ];

    // Calculate active filters count
    const activeFiltersCount = () => {
        let count = 0;

        if (tempFilters.status !== "All") count++;
        if (tempFilters.department !== "All Departments") count++;
        if (tempFilters.designation !== "All Designations") count++;
        if (tempFilters.sortBy !== "Last 7 Days") count++;
        if (tempFilters.joiningDate?.start && tempFilters.joiningDate?.end) count++;
        if (tempFilters.createdDate?.start && tempFilters.createdDate?.end) count++;
        // if (tempFilters.company?.id) count++;
        if (tempFilters.gender !== "All Genders") count++;

        return count;
    };

    // Handle filter changes
    const handleStatusChange = (value: string) => {
        setTempFilters(prev => ({ ...prev, status: value }));
    };

    const handleDepartmentChange = (value: string) => {
        setTempFilters(prev => ({ ...prev, department: value }));
    };

    const handleDesignationChange = (value: string) => {
        setTempFilters(prev => ({ ...prev, designation: value }));
    };

    const handleSortByChange = (value: string) => {
        setTempFilters(prev => ({ ...prev, sortBy: value }));
    };

    const handleCompanyChange = (selectedOption: any) => {
        const [id, type] = selectedOption.value.split("-");
        const company = data.allCompanies.find(c => c.id === parseInt(id) && c.type === type);
        if (company) {
            setTempFilters(prev => ({
                ...prev,
                company: {
                    id: company.id,
                    name: company.name,
                    type: company.type
                }
            }));
        }
    };

    const handleGenderChange = (value: string) => {
        setTempFilters(prev => ({ ...prev, gender: value }));
    };

    const handleJoiningDateChange = (start: moment.Moment, end: moment.Moment) => {
        setTempFilters(prev => ({
            ...prev,
            joiningDate: {
                start: start.format("YYYY-MM-DD"),
                end: end.format("YYYY-MM-DD")
            }
        }));
    };

    const handleCreatedDateChange = (start: moment.Moment, end: moment.Moment) => {
        setTempFilters(prev => ({
            ...prev,
            createdDate: {
                start: start.format("YYYY-MM-DD"),
                end: end.format("YYYY-MM-DD")
            }
        }));
    };

    const handleClearJoiningDate = () => {
        setTempFilters(prev => ({ ...prev, joiningDate: null }));
    };

    const handleClearCreatedDate = () => {
        setTempFilters(prev => ({ ...prev, createdDate: null }));
    };

    const handleApply = () => {
        onApply(tempFilters);
        onClose();
    };

    const handleReset = () => {
        const defaultCompany = data.allCompanies.length > 0
            ? {
                id: data.allCompanies[0].id,
                name: data.allCompanies[0].name,
                type: data.allCompanies[0].type
            }
            : null;

        setTempFilters({
            status: "All",
            department: "All Departments",
            designation: "All Designations",
            sortBy: "Last 7 Days",
            joiningDate: null,
            createdDate: null,
            company: defaultCompany,
            gender: "All Genders"
        });
        onReset();
        onClose();
    };

    const getCompanyDisplay = () => {
        if (tempFilters.company) {
            return `${tempFilters.company.name} - ${tempFilters.company.type}`;
        }
        return "All Companies";
    };

    return (
        <div
            className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
            id="employeeFilterPanel"
            style={{ maxWidth: "50%", width: "100%" }}
            aria-labelledby="employeeFilterPanelLabel"
        >
            <div className="offcanvas-header">
                <h5 className="offcanvas-title" id="employeeFilterPanelLabel">
                    Employee Filters
                    {activeFiltersCount() > 0 && (
                        <span className="badge bg-primary rounded-pill ms-2">
                            {activeFiltersCount()}
                        </span>
                    )}
                </h5>
                <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="offcanvas"
                    onClick={onClose}
                ></button>
            </div>
            <div className="offcanvas-body" style={{ padding: "1.5rem" }}>
                <div className="row g-3">
                    {/* Status Filter */}
                    <div className="col-md-6 col-12">
                        <label className="form-label">Status</label>
                        <CommonSelect
                            key={`status-${selectKey}`}
                            className="select"
                            options={statusOptions}
                            defaultValue={statusOptions.find(
                                status => status.value === tempFilters.status
                            )}
                            onChange={(selected: any) => handleStatusChange(selected.value)}
                        />
                    </div>

                    {/* Department Filter */}
                    <div className="col-md-6 col-12">
                        <label className="form-label">Department</label>
                        <CommonSelect
                            key={`department-${selectKey}`}
                            className="select"
                            options={departmentOptions}
                            defaultValue={departmentOptions.find(
                                dept => dept.value === tempFilters.department
                            )}
                            onChange={(selected: any) => handleDepartmentChange(selected.value)}
                        />
                    </div>

                    {/* Designation Filter */}
                    <div className="col-md-6 col-12">
                        <label className="form-label">Designation</label>
                        <CommonSelect
                            key={`designation-${selectKey}`}
                            className="select"
                            options={designationOptions}
                            defaultValue={designationOptions.find(
                                desig => desig.value === tempFilters.designation
                            )}
                            onChange={(selected: any) => handleDesignationChange(selected.value)}
                        />
                    </div>

                    {/* Gender Filter */}
                    <div className="col-md-6 col-12">
                        <label className="form-label">Gender</label>
                        <CommonSelect
                            key={`gender-${selectKey}`}
                            className="select"
                            options={genderOptions}
                            defaultValue={genderOptions.find(
                                gender => gender.value === tempFilters.gender
                            )}
                            onChange={(selected: any) => handleGenderChange(selected.value)}
                        />
                    </div>

                    {/* Joining Date Filter */}
                    <div className="col-md-6 col-12">
                        <label className="form-label">Joining Date</label>
                        <div className="input-icon-end position-relative">
                            <PredefinedDateRanges
                                onDateChange={handleJoiningDateChange}
                                startDate={
                                    tempFilters.joiningDate
                                        ? moment(tempFilters.joiningDate.start)
                                        : undefined
                                }
                                endDate={
                                    tempFilters.joiningDate
                                        ? moment(tempFilters.joiningDate.end)
                                        : undefined
                                }
                            />
                            <span className="input-icon-addon">
                                <i className="ti ti-chevron-down" />
                            </span>
                        </div>
                        {tempFilters.joiningDate && (
                            <button
                                className="btn btn-sm btn-light mt-2"
                                onClick={handleClearJoiningDate}
                            >
                                Clear Filter
                            </button>
                        )}
                    </div>

                    {/* Created Date Filter */}
                    <div className="col-md-6 col-12">
                        <label className="form-label">Created Date</label>
                        <div className="input-icon-end position-relative">
                            <PredefinedDateRanges
                                onDateChange={handleCreatedDateChange}
                                startDate={
                                    tempFilters.createdDate
                                        ? moment(tempFilters.createdDate.start)
                                        : undefined
                                }
                                endDate={
                                    tempFilters.createdDate
                                        ? moment(tempFilters.createdDate.end)
                                        : undefined
                                }
                            />
                            <span className="input-icon-addon">
                                <i className="ti ti-chevron-down" />
                            </span>
                        </div>
                        {tempFilters.createdDate && (
                            <button
                                className="btn btn-sm btn-light mt-2"
                                onClick={handleClearCreatedDate}
                            >
                                Clear Filter
                            </button>
                        )}
                    </div>

                    {/* Sort By Filter */}
                    <div className="col-md-6 col-12">
                        <label className="form-label">Sort By</label>
                        <CommonSelect
                            key={`sort-${selectKey}`}
                            className="select"
                            options={sortOptions}
                            defaultValue={sortOptions.find(
                                sort => sort.value === tempFilters.sortBy
                            )}
                            onChange={(selected: any) => handleSortByChange(selected.value)}
                        />
                    </div>
                </div>

                {/* Current Filters Summary */}
                {activeFiltersCount() > 0 && (
                    <div className="mt-4 p-3 bg-light rounded">
                        <h6 className="mb-2">Active Filters:</h6>
                        <div className="d-flex flex-wrap gap-2">
                            {tempFilters.status !== "All" && (
                                <span className="badge bg-primary">
                                    Status: {tempFilters.status}
                                </span>
                            )}
                            {tempFilters.department !== "All Departments" && (
                                <span className="badge bg-success">
                                    Department: {tempFilters.department}
                                </span>
                            )}
                            {tempFilters.designation !== "All Designations" && (
                                <span className="badge bg-info">
                                    Designation: {tempFilters.designation}
                                </span>
                            )}
                            {tempFilters.company && (
                                <span className="badge bg-warning">
                                    Company: {tempFilters.company.name}
                                </span>
                            )}
                            {tempFilters.sortBy !== "Last 7 Days" && (
                                <span className="badge bg-secondary">
                                    Sort: {tempFilters.sortBy}
                                </span>
                            )}
                            {tempFilters.joiningDate && (
                                <span className="badge bg-dark">
                                    Joining: {tempFilters.joiningDate.start} to {tempFilters.joiningDate.end}
                                </span>
                            )}
                            {tempFilters.createdDate && (
                                <span className="badge bg-dark">
                                    Created: {tempFilters.createdDate.start} to {tempFilters.createdDate.end}
                                </span>
                            )}
                            {tempFilters.gender !== "All Genders" && (
                                <span className="badge bg-purple">
                                    Gender: {tempFilters.gender === "M" ? "Male" : tempFilters.gender === "F" ? "Female" : "Other"}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-between mt-4">
                    <button
                        className="btn btn-secondary"
                        onClick={handleReset}
                    >
                        Reset All
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleApply}
                    >
                        Apply Filters
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmployeeFilterPanel;